import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Plus } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { format } from "date-fns";
import { PlanItem } from "@/components/dailyplan/PlanItem";

const fmtLong = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function UserTabs({ users, selected, onSelect, me }) {
    return (
        <div className="flex gap-2 flex-wrap" data-testid="plan-user-tabs">
            {users.map((u) => (
                <button key={u.id} onClick={() => onSelect(u.id)} data-testid={`plan-user-tab-${u.id}`}
                    className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-sm transition-colors ${selected === u.id ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"}`}>
                    <UserAvatar user={u} size={22} /> {u.first_name}{u.id === me ? " (tu)" : ""}
                </button>
            ))}
        </div>
    );
}

function QuickAdd({ onAdd }) {
    const [v, setV] = useState("");
    const submit = async (e) => { e.preventDefault(); if (!v.trim()) return; await onAdd(v.trim()); setV(""); };
    return (
        <form onSubmit={submit} className="flex gap-2" data-testid="plan-quick-add">
            <input value={v} onChange={(e) => setV(e.target.value)} placeholder="Scrivi una nuova task e premi Invio…" data-testid="plan-quick-add-input"
                className="flex-1 text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-neutral-400" />
            <button type="submit" disabled={!v.trim()} data-testid="plan-quick-add-btn" className="text-xs px-3 rounded-md bg-neutral-900 text-white disabled:opacity-40 inline-flex items-center gap-1"><Plus size={13} /> Aggiungi</button>
        </form>
    );
}

export default function DailyPlans() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [users, setUsers] = useState([]);
    const [uid, setUid] = useState(params.get("u") || user?.id);
    const [day, setDay] = useState(params.get("d") || "");
    const [plan, setPlan] = useState(null);
    const dragId = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/daily-plans", { params: { user_id: uid, day: day || undefined } });
            setPlan(data);
            if (data.date !== day) setDay(data.date);
        } catch (e) { toast.error(extractError(e)); }
    }, [uid, day]);

    useEffect(() => { api.get("/daily-plans/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (day) setParams({ u: uid, d: day }, { replace: true }); }, [uid, day, setParams]);

    const act = async (fn) => { try { await fn(); } catch (e) { toast.error(extractError(e)); } load(); };
    const onDrop = async (targetId) => {
        const src = dragId.current;
        dragId.current = null;
        if (!src || src === targetId) return;
        const all = plan.items.map((i) => i.id);
        all.splice(all.indexOf(targetId), 0, ...all.splice(all.indexOf(src), 1));
        setPlan({ ...plan, items: all.map((id) => plan.items.find((i) => i.id === id)) });
        const ids = all.filter((id) => !plan.items.find((i) => i.id === id).moved_to);
        act(() => api.post("/daily-plans/reorder", { date: plan.date, ids }));
    };

    if (!plan) return <div className="p-6 text-sm text-neutral-400">Caricamento…</div>;
    const done = plan.items.filter((i) => i.completed).length;
    const owner = users.find((u) => u.id === plan.user_id);
    return (
        <div className="p-6 space-y-6 max-w-3xl" data-testid="daily-plans-page">
            <div className="space-y-4">
                <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">Piani giornalieri</h1>
                <div className="flex items-center gap-2">
                    <button onClick={() => setDay(plan.prev_date)} data-testid="plan-prev-day" className="p-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50"><ChevronLeft size={15} /></button>
                    <div className="text-base font-medium capitalize min-w-[16rem] text-center" data-testid="plan-date-label">{fmtLong(plan.date)}</div>
                    <button onClick={() => setDay(plan.next_date)} data-testid="plan-next-day" className="p-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50"><ChevronRight size={15} /></button>
                    <Popover>
                        <PopoverTrigger asChild><button data-testid="plan-date-picker" className="p-1.5 rounded-md border border-neutral-200 hover:bg-neutral-50"><CalendarDays size={15} /></button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" locale={itLocale} weekStartsOn={1} selected={new Date(`${plan.date}T00:00:00`)}
                                disabled={(d) => d.getDay() === 0 || d.getDay() === 6} onSelect={(d) => d && setDay(format(d, "yyyy-MM-dd"))} />
                        </PopoverContent>
                    </Popover>
                    {plan.date !== plan.today && <button onClick={() => setDay(plan.today)} data-testid="plan-today" className="text-xs text-neutral-500 hover:text-neutral-900 underline ml-1">Oggi</button>}
                </div>
                <UserTabs users={users} selected={plan.user_id} onSelect={setUid} me={user?.id} />
            </div>
            {plan.editable && <QuickAdd onAdd={(title) => act(() => api.post("/daily-plans/items", { date: plan.date, title }))} />}
            {plan.editable && plan.is_past && (
                <div className="text-xs text-neutral-500" data-testid="plan-past-note">
                    Giorno passato: puoi ancora correggerlo. Le task non completate aggiunte ora passeranno al giorno lavorativo successivo dalla prossima mezzanotte.
                </div>
            )}
            {!plan.editable && (
                <div className="text-xs text-neutral-500" data-testid="plan-readonly-note">
                    {`Piano di ${owner?.first_name || "un altro utente"}: puoi consultarlo ma non modificarlo.`}
                </div>
            )}
            <section className="space-y-2">
                <div className="text-xs text-neutral-500" data-testid="plan-counter">{done} di {plan.items.length} completate</div>
                {plan.items.length === 0 && <div className="text-sm text-neutral-400 border border-dashed border-neutral-300 rounded-md p-6 text-center bg-white" data-testid="plan-empty">Nessuna task pianificata per questo giorno.</div>}
                <ul className="space-y-1.5" data-testid="plan-items">
                    {plan.items.map((it) => (
                        <PlanItem key={it.id} it={it} editable={plan.editable && !it.moved_to}
                            onToggle={(v) => act(() => api.patch(`/daily-plans/items/${it.id}`, { completed: v }))}
                            onRename={(title) => act(() => api.patch(`/daily-plans/items/${it.id}`, { title }))}
                            onDelete={() => act(() => api.delete(`/daily-plans/items/${it.id}`))}
                            onReplace={() => act(() => api.post(`/daily-plans/items/${it.id}/replace-with-subtasks`))}
                            onKeep={() => act(() => api.post(`/daily-plans/items/${it.id}/keep`))}
                            dragProps={{ draggable: plan.editable && !it.moved_to, onDragStart: () => { dragId.current = it.id; }, onDragOver: (e) => e.preventDefault(), onDrop: () => onDrop(it.id) }} />
                    ))}
                </ul>
            </section>
        </div>
    );
}
